import React, {
  useRef, useEffect, useLayoutEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectWebsiteId } from '../../store/slices/website';

import CLFontIcon from '../../components/CLFontIcon';
import ListingListState from '../../components-shared/ListingListState';

import {
  selectListings,
  selectListingsLoading,
  getListings,
} from '../../store/slices/listings';

import {
  Container,
  List,
  Button,
} from './styled';

import Item from './Item';
import ItemPlaceholder from './ItemPlaceholder';

const ListingsPreview = () => {
  const dispatch = useDispatch();
  const listContainerRef = useRef(null);

  const websiteId = useSelector(selectWebsiteId);
  const listings = useSelector(selectListings);
  const isLoading = useSelector(selectListingsLoading);

  const [selected, setSelected] = useState(0);
  const [columns, setColumns] = useState(3);
  const [itemSize, setItemSize] = useState({
    width: 'calc(33% - 21px)',
    height: '240px',
  });

  const isFirst = selected === 0;
  const isLast = selected === listings.length - columns;

  function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
      function updateSize() {
        setSize([window.innerWidth, window.innerHeight]);
      }
      window.addEventListener('resize', updateSize);
      updateSize();
      return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
  }

  const [width, height] = useWindowSize();

  useEffect(() => {
    if (!width || !height) {
      return;
    }

    if (width > 769) {
      setColumns(3);
      setItemSize({
        width: 'calc(33% - 21px)',
        height: '240px',
      });
    }

    if (width < 768) {
      setColumns(2);
      setItemSize({
        width: 'calc(50% - 24px)',
        height: '240px',
      });
    }
    if (width < 560) {
      setColumns(1);
      setItemSize({
        width: 'calc(100% - 24px)',
        height: '240px',
      });
    }
  }, [width, height]);

  useEffect(() => {
    dispatch(getListings({
      website_id: websiteId,
      page: 1,
    }));
  }, [websiteId, dispatch]);

  useEffect(() => {
    if (!listContainerRef?.current) {
      return;
    }
    const { current } = listContainerRef;

    current.scrollTo({
      left: ((current.clientWidth / columns) * selected),
      behavior: 'smooth',
    });
  }, [selected, columns]);

  if (!listings.length) {
    if (isLoading) {
      return (
        <List>
          {[1, 2, 3].map((item) => <ItemPlaceholder key={item} itemSize={itemSize} />)}
        </List>
      );
    }

    return <ListingListState type="no_listings" />;
  }

  const prev = () => {
    setSelected((index) => (index > 0 ? index - 1 : 0));
  };

  const next = () => {
    const last = listings.length - columns;
    setSelected((index) => (index < last ? index + 1 : last));
  };

  return (
    <Container>
      {!isFirst && (
        <Button position="left" onClick={() => prev()}>
          <CLFontIcon icon="caret-left" />
        </Button>
      )}
      {!isLast && (
        <Button position="right" onClick={() => next()}>
          <CLFontIcon icon="caret-right" />
        </Button>
      )}

      <List ref={listContainerRef}>
        {listings.map((item) => (
          <Item
            key={item.listing_id}
            item={item}
            websiteId={websiteId}
            itemSize={itemSize}
          />
        ))}
      </List>
    </Container>
  );
};

export default ListingsPreview;
